import React, { useEffect, useState, useRef, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { MapContainer, TileLayer, Tooltip as LeafletTooltip, useMap, Marker, useMapEvents } from 'react-leaflet';
import { ChevronDown, Play, Pause, ChevronLeft, ChevronRight, LocateFixed, Database, Info, MoreVertical, Layers, X, MapPin, Lightbulb, Cpu, Check } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { MapActionsMenu } from './MapActionsMenu';
import { MapAiEditPanel } from './MapAiEditPanel';
import { useAthena } from '../../hooks/useAthena';
import { useTheme } from '../../hooks/useTheme';
import { getWeatherTileUrl, getOWMLayerForOverlay } from '../../hooks/useWeatherData';
import type { ModelOverride } from './ModelRunSelector';
import type { LocationOverride } from './LocationSelector';
import { overlayConfigs, MODEL_COLORS, WEATHER_LAYERS, POI_CATEGORIES, POI_DATA, POI_ICON_SVGS, parseLocation, parseVariable, parsePOIs, parseWeatherLayers } from './mapData';
import type { POICategory } from './mapData';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cn } from '../ui/Button';
interface MapWidgetProps {
  id: string;
  onRemove?: () => void;
  onDuplicate?: () => void;
  onToggleBackground?: () => void;
  isBackground?: boolean;
  onParameterChange?: () => void;
  onDoubleClickHandle?: () => void;
  modelOverride?: ModelOverride | null;
  locationOverride?: LocationOverride | null;
  readOnly?: boolean;
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchEnd?: React.TouchEventHandler;
  initialOverlay?: string;
  initialPOIs?: string[];
}
const glassStyle: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)'
};
const glassStyleDark: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)'
};
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 100);
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [map]);
  return null;
}
function MapController({
  flyTarget
}: {
  flyTarget: {
    lat: number;
    lng: number;
    zoom: number;
  } | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (flyTarget) map.flyTo([flyTarget.lat, flyTarget.lng], flyTarget.zoom, {
      duration: 1.5,
      easeLinearity: 0.25
    });
  }, [flyTarget, map]);
  return null;
}
const OWM_KEY = '4fe653354075b44397ed6f89fc880109';
interface CursorValue {
  value: number;
  unit: string;
  label: string;
}
function MapCursorTooltip({
  overlay
}: {
  overlay: string;
}) {
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [latlng, setLatlng] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [cursorValue, setCursorValue] = useState<CursorValue | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<Record<string, CursorValue>>({});
  const abortRef = useRef<AbortController | null>(null);
  const getOverlayConfig = (ov: string) => {
    switch (ov) {
      case 'Air Temperature 2m':
        return {
          field: 'temp',
          unit: '°C',
          label: 'Temperature'
        };
      case 'Air Pressure Sea Level':
        return {
          field: 'pressure',
          unit: 'hPa',
          label: 'Pressure'
        };
      case 'Geopotential Height 500hPa':
        return {
          field: 'wind_speed',
          unit: 'm/s',
          label: 'Wind Speed'
        };
      case 'Surface Solar Radiation':
        return {
          field: 'clouds',
          unit: '%',
          label: 'Cloud Cover'
        };
      default:
        return {
          field: 'temp',
          unit: '°C',
          label: 'Temperature'
        };
    }
  };
  const fetchValue = (lat: number, lng: number) => {
    const key = `${lat.toFixed(1)},${lng.toFixed(1)}`;
    if (cacheRef.current[key]) {
      setCursorValue(cacheRef.current[key]);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${OWM_KEY}&units=metric`, {
      signal: controller.signal
    }).then(res => res.json()).then(json => {
      const config = getOverlayConfig(overlay);
      let value: number;
      switch (config.field) {
        case 'temp':
          value = json.main?.temp ?? 0;
          break;
        case 'pressure':
          value = json.main?.pressure ?? 0;
          break;
        case 'wind_speed':
          value = json.wind?.speed ?? 0;
          break;
        case 'clouds':
          value = json.clouds?.all ?? 0;
          break;
        default:
          value = json.main?.temp ?? 0;
      }
      const result: CursorValue = {
        value: +value.toFixed(1),
        unit: config.unit,
        label: config.label
      };
      cacheRef.current[key] = result;
      setCursorValue(result);
      setLoading(false);
    }).catch(err => {
      if (err.name !== 'AbortError') setLoading(false);
    });
  };
  useMapEvents({
    mousemove(e) {
      const container = e.originalEvent.target as HTMLElement;
      const rect = container.closest('.leaflet-container')?.getBoundingClientRect();
      if (rect) setPosition({
        x: e.originalEvent.clientX - rect.left,
        y: e.originalEvent.clientY - rect.top
      });
      setLatlng({
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchValue(e.latlng.lat, e.latlng.lng), 400);
    },
    mouseout() {
      setPosition(null);
      setLatlng(null);
      setCursorValue(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  });
  if (!position || !latlng) return null;
  return <div className="absolute pointer-events-none z-[900]" style={{
    left: position.x + 16,
    top: position.y - 12
  }} data-id="element-1602">
      <div className={cn('rounded-lg px-3 py-2 text-[11px] shadow-xl min-w-[140px]', isLight ? 'border border-black/[0.08] shadow-slate-300/50' : 'border border-white/[0.12] shadow-black/50')} style={{
      background: isLight ? 'rgba(255, 255, 255, 0.92)' : 'rgba(11, 17, 32, 0.92)',
      backdropFilter: 'blur(16px)'
    }} data-id="element-1603">
        <div className="flex items-baseline gap-1.5 mb-1" data-id="element-1604">
          {loading && !cursorValue ? <span className={isLight ? 'text-slate-400 text-[10px]' : 'text-white/40 text-[10px]'} data-id="element-1605">
              Loading…
            </span> : cursorValue ? <>
              <span className={cn('font-bold text-sm tabular-nums', isLight ? 'text-slate-900' : 'text-white')} data-id="element-1606">
                {cursorValue.value}
              </span>
              <span className={cn('font-medium', isLight ? 'text-slate-500' : 'text-white/50')} data-id="element-1607">
                {cursorValue.unit}
              </span>
            </> : <span className={isLight ? 'text-slate-300 text-[10px]' : 'text-white/30 text-[10px]'} data-id="element-1608">
              Hover to sample
            </span>}
        </div>
        {cursorValue && <div className={cn('text-[10px] mb-1.5', isLight ? 'text-slate-400' : 'text-white/40')} data-id="element-1609">
            {cursorValue.label}
          </div>}
        <div className={cn('text-[9px] font-mono tabular-nums border-t pt-1', isLight ? 'text-slate-300 border-black/[0.06]' : 'text-white/25 border-white/[0.06]')} data-id="element-1610">
          {latlng.lat.toFixed(2)}°, {latlng.lng.toFixed(2)}°
        </div>
      </div>
    </div>;
}
function createPOIDivIcon(category: POICategory): L.DivIcon {
  const svgPath = POI_ICON_SVGS[category.id] || '';
  const html = `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:8px;background:${category.color}30;border:1.5px solid ${category.color}80;color:${category.color};backdrop-filter:blur(8px);box-shadow:0 2px 8px rgba(0,0,0,0.4),0 0 12px ${category.color}20;"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svgPath}</svg></div>`;
  return L.divIcon({
    html,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
}
export const MapWidget = forwardRef<HTMLDivElement, MapWidgetProps>(({
  id,
  onRemove,
  onDuplicate,
  onToggleBackground,
  isBackground,
  onParameterChange,
  onDoubleClickHandle,
  modelOverride,
  locationOverride,
  readOnly,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  initialOverlay,
  initialPOIs,
  ...props
}, ref) => {
  const {
    generateText
  } = useAthena();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  // Theme-conditional glass styles
  const glassStyle: React.CSSProperties = isLight ? {
    background: 'rgba(255, 255, 255, 0.80)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  } : {
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const glassStyleDark: React.CSSProperties = isLight ? {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  } : {
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  // Map tile URL based on theme
  const mapTileUrl = isLight ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png' : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState(initialOverlay || 'Geopotential Height 500hPa');
  const [overlayMenuOpen, setOverlayMenuOpen] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [poiPanelOpen, setPoiPanelOpen] = useState(false);
  const [activePOIs, setActivePOIs] = useState<Set<string>>(new Set(initialPOIs || []));
  const [showTip, setShowTip] = useState(true);
  const [tipVisible, setTipVisible] = useState(false);
  useEffect(() => {
    const fadeIn = setTimeout(() => setTipVisible(true), 1200);
    const fadeOut = setTimeout(() => setTipVisible(false), 8000);
    const remove = setTimeout(() => setShowTip(false), 8600);
    return () => {
      clearTimeout(fadeIn);
      clearTimeout(fadeOut);
      clearTimeout(remove);
    };
  }, []);
  const actionsRef = useRef<HTMLDivElement>(null);
  const overlayButtonRef = useRef<HTMLButtonElement>(null);
  const config = overlayConfigs[selectedOverlay];
  const owmLayer = getOWMLayerForOverlay(selectedOverlay);
  const owmTileUrl = getWeatherTileUrl(owmLayer);
  const [layersPanelOpen, setLayersPanelOpen] = useState(false);
  const [activeLayers, setActiveLayers] = useState<Set<string>>(new Set(['wind100m']));
  const [colorScaleMode, setColorScaleMode] = useState<'fixed' | 'dynamic'>('dynamic');
  const [colorScaleOpen, setColorScaleOpen] = useState(false);
  const colorScaleRef = useRef<HTMLDivElement>(null);
  const layersPanelRef = useRef<HTMLDivElement>(null);
  const layersButtonRef = useRef<HTMLButtonElement>(null);
  const poiPanelRef = useRef<HTMLDivElement>(null);
  const poiButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedMapModel, setSelectedMapModel] = useState<string>('EPT-2');
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownButtonRef = useRef<HTMLButtonElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const availableModels = modelOverride?.modelNames ?? ['EPT-2', 'EC-IFS', 'GFS'];
  useEffect(() => {
    if (!availableModels.includes(selectedMapModel)) setSelectedMapModel(availableModels[0] || 'EPT-2');
  }, [availableModels, selectedMapModel]);
  const toggleLayer = (layerId: string) => {
    setActiveLayers(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) next.delete(layerId);else next.add(layerId);
      return next;
    });
    onParameterChange?.();
  };
  const togglePOI = (categoryId: string) => {
    setActivePOIs(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);else next.add(categoryId);
      return next;
    });
  };
  const toggleAllPOIs = () => {
    if (activePOIs.size === POI_CATEGORIES.length) setActivePOIs(new Set());else setActivePOIs(new Set(POI_CATEGORIES.map(c => c.id)));
  };
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [flyTarget, setFlyTarget] = useState<{
    lat: number;
    lng: number;
    zoom: number;
  } | null>(null);
  // Compact mode: collapse controls when widget is narrow
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setIsCompact(w < 420);
      setIsMedium(w >= 420 && w < 600);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // Location override — fly map to the selected region
  const LOCATION_COORDS: Record<string, {
    lat: number;
    lng: number;
    zoom: number;
  }> = {
    Germany: {
      lat: 51.2,
      lng: 10.4,
      zoom: 6
    },
    'United Kingdom': {
      lat: 54.0,
      lng: -2.0,
      zoom: 6
    },
    France: {
      lat: 46.6,
      lng: 2.2,
      zoom: 6
    },
    Spain: {
      lat: 40.0,
      lng: -3.7,
      zoom: 6
    },
    Italy: {
      lat: 42.5,
      lng: 12.5,
      zoom: 6
    },
    Netherlands: {
      lat: 52.1,
      lng: 5.3,
      zoom: 7
    },
    Poland: {
      lat: 52.0,
      lng: 19.4,
      zoom: 6
    },
    Sweden: {
      lat: 62.0,
      lng: 15.0,
      zoom: 5
    },
    Norway: {
      lat: 64.0,
      lng: 12.0,
      zoom: 5
    },
    Nordic: {
      lat: 62.0,
      lng: 15.0,
      zoom: 4
    },
    'Iberian Peninsula': {
      lat: 39.5,
      lng: -3.0,
      zoom: 5
    },
    'Central Eastern Europe': {
      lat: 50.0,
      lng: 18.0,
      zoom: 5
    },
    Balkans: {
      lat: 42.5,
      lng: 21.0,
      zoom: 5
    },
    Berlin: {
      lat: 52.52,
      lng: 13.4,
      zoom: 10
    },
    London: {
      lat: 51.5,
      lng: -0.12,
      zoom: 10
    },
    Paris: {
      lat: 48.86,
      lng: 2.35,
      zoom: 10
    },
    Madrid: {
      lat: 40.42,
      lng: -3.7,
      zoom: 10
    },
    Rome: {
      lat: 41.9,
      lng: 12.5,
      zoom: 10
    },
    Amsterdam: {
      lat: 52.37,
      lng: 4.9,
      zoom: 10
    },
    Warsaw: {
      lat: 52.23,
      lng: 21.0,
      zoom: 10
    },
    Stockholm: {
      lat: 59.33,
      lng: 18.07,
      zoom: 10
    },
    Oslo: {
      lat: 59.91,
      lng: 10.75,
      zoom: 10
    },
    Munich: {
      lat: 48.14,
      lng: 11.58,
      zoom: 10
    },
    Hamburg: {
      lat: 53.55,
      lng: 9.99,
      zoom: 10
    },
    'North Sea': {
      lat: 56.0,
      lng: 3.0,
      zoom: 5
    },
    'Baltic Sea': {
      lat: 58.0,
      lng: 20.0,
      zoom: 5
    },
    Mediterranean: {
      lat: 38.0,
      lng: 15.0,
      zoom: 5
    }
  };
  useEffect(() => {
    if (locationOverride) {
      const coords = LOCATION_COORDS[locationOverride.regionName];
      if (coords) {
        setFlyTarget({
          lat: coords.lat,
          lng: coords.lng,
          zoom: coords.zoom
        });
      }
    }
  }, [locationOverride]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) setActionsMenuOpen(false);
      if (layersPanelOpen && layersPanelRef.current && !layersPanelRef.current.contains(e.target as Node) && layersButtonRef.current && !layersButtonRef.current.contains(e.target as Node)) setLayersPanelOpen(false);
      if (poiPanelOpen && poiPanelRef.current && !poiPanelRef.current.contains(e.target as Node) && poiButtonRef.current && !poiButtonRef.current.contains(e.target as Node)) setPoiPanelOpen(false);
      if (colorScaleOpen && colorScaleRef.current && !colorScaleRef.current.contains(e.target as Node)) setColorScaleOpen(false);
      if (modelDropdownOpen && modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node) && modelDropdownButtonRef.current && !modelDropdownButtonRef.current.contains(e.target as Node)) setModelDropdownOpen(false);
      if (overlayMenuOpen && overlayButtonRef.current && !overlayButtonRef.current.contains(e.target as Node)) setOverlayMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [layersPanelOpen, poiPanelOpen, colorScaleOpen, modelDropdownOpen, overlayMenuOpen]);
  const handleAiEdit = async () => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    setAiResult('');
    const lower = aiPrompt.toLowerCase();
    const detectedVariable = parseVariable(aiPrompt);
    if (detectedVariable && detectedVariable !== selectedOverlay) {
      setSelectedOverlay(detectedVariable);
      onParameterChange?.();
    }
    const mentionsSolar = /solar|irradiance|sunshine|clear sky/i.test(lower);
    const mentionsTemp = /temperature|temp|ground temp/i.test(lower);
    const mentionsCombine = /with|and|combine|overlay|on top|plus/i.test(lower);
    if (mentionsSolar && mentionsTemp && mentionsCombine) {
      setSelectedOverlay('Air Temperature 2m');
      setActiveLayers(prev => {
        const next = new Set(prev);
        next.add('clouds');
        return next;
      });
      onParameterChange?.();
    } else if (mentionsSolar && !detectedVariable) {
      setSelectedOverlay('Surface Solar Radiation');
      onParameterChange?.();
    }
    const detectedLocation = parseLocation(aiPrompt);
    if (detectedLocation) {
      setFlyTarget({
        lat: detectedLocation.lat,
        lng: detectedLocation.lng,
        zoom: detectedLocation.zoom
      });
      setAiEditOpen(false);
      setAiResult('');
      setAiPrompt('');
    }
    const detectedPOIs = parsePOIs(aiPrompt);
    if (detectedPOIs.length > 0) setActivePOIs(new Set(detectedPOIs));
    const detectedLayers = parseWeatherLayers(aiPrompt);
    if (detectedLayers.length > 0) setActiveLayers(prev => {
      const next = new Set(prev);
      for (const layerId of detectedLayers) next.add(layerId);
      return next;
    });
    try {
      const appliedOverlay = detectedVariable || selectedOverlay;
      const appliedLocation = detectedLocation?.name || 'current view';
      const appliedPOINames = detectedPOIs.map(id => POI_CATEGORIES.find(c => c.id === id)?.label || id);
      const appliedLayerNames = detectedLayers.map(id => WEATHER_LAYERS.find(l => l.id === id)?.label || id);
      const actionsSummary = [detectedVariable ? `switched overlay to ${appliedOverlay}` : '', mentionsSolar && mentionsTemp && mentionsCombine ? 'combined Temperature base with Cloud Cover overlay' : '', detectedLocation ? `moved map to ${detectedLocation.name}` : '', appliedPOINames.length > 0 ? `enabled POIs: ${appliedPOINames.join(', ')}` : '', appliedLayerNames.length > 0 ? `enabled layers: ${appliedLayerNames.join(', ')}` : ''].filter(Boolean).join('; ');
      const result = await generateText(`You are Athena, the intelligent map visualization assistant for the Jua energy trading platform. The user is editing a map widget. Current overlay: "${appliedOverlay}" over ${appliedLocation}. Available overlays: ${Object.keys(overlayConfigs).join(', ')}. Available POI categories: ${POI_CATEGORIES.map(c => c.label).join(', ')}. Available weather layers: ${WEATHER_LAYERS.map(l => l.label).join(', ')}. The system has already applied these changes: ${actionsSummary || 'none detected'}. Confirm what was done in 1-2 concise sentences.`, aiPrompt);
      setAiResult(result);
      if (!detectedVariable) {
        for (const key of Object.keys(overlayConfigs)) {
          if (result.toLowerCase().includes(key.toLowerCase()) && key !== selectedOverlay) {
            setSelectedOverlay(key);
            onParameterChange?.();
            break;
          }
        }
      }
    } catch {
      setAiResult('Something went wrong. Please try again.');
    } finally {
      setAiLoading(false);
      if (!detectedLocation) {
        setTimeout(() => {
          setAiEditOpen(false);
          setAiResult('');
          setAiPrompt('');
        }, 1500);
      }
    }
  };
  const activeOWMTiles = WEATHER_LAYERS.filter(l => activeLayers.has(l.id));
  const visiblePOIs = POI_DATA.filter(p => activePOIs.has(p.categoryId));
  const poiCategoryMap = Object.fromEntries(POI_CATEGORIES.map(c => [c.id, c]));
  // Portaled dropdown glass style
  const portalGlassStyle: React.CSSProperties = isLight ? {
    background: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  } : {
    background: 'rgba(15, 23, 42, 0.85)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const mapContent = <div ref={mapContainerRef} className={cn('jua-map-widget relative h-full w-full', isLight ? 'bg-slate-100' : 'bg-slate-900')} data-no-drag data-id="element-1611">
        <MapContainer center={[54, -2]} zoom={6} className="h-full w-full z-0" zoomControl={false} attributionControl={false} data-id="element-1612">
          <TileLayer url={mapTileUrl} data-id="element-1613" />
          <TileLayer key={owmLayer} url={owmTileUrl} opacity={0.85} zIndex={10} data-id="element-1614" />
          {activeOWMTiles.map((layer, idx) => {
        if (layer.owmLayer === owmLayer) return null;
        return <TileLayer key={`extra-${layer.id}`} url={getWeatherTileUrl(layer.owmLayer)} opacity={0.4} zIndex={11 + idx} data-id="element-1615" />;
      })}
          {visiblePOIs.map((poi, idx) => {
        const cat = poiCategoryMap[poi.categoryId];
        if (!cat) return null;
        return <Marker key={`poi-${poi.categoryId}-${idx}`} position={[poi.lat, poi.lng]} icon={createPOIDivIcon(cat)} data-id="element-1616">
                <LeafletTooltip direction="top" offset={[0, -18]} className={isLight ? 'poi-tooltip-light' : 'poi-tooltip'} data-id="element-1617">
                  <div style={{
              fontFamily: 'system-ui',
              fontSize: '11px'
            }} data-id="element-1618">
                    <strong style={{
                display: 'block',
                marginBottom: '2px'
              }} data-id="element-1619">
                      {poi.name}
                    </strong>
                    {poi.detail && <span style={{
                color: isLight ? '#64748b' : '#94a3b8',
                fontSize: '10px'
              }} data-id="element-1620">
                        {poi.detail}
                      </span>}
                  </div>
                </LeafletTooltip>
              </Marker>;
      })}
          <MapInvalidator data-id="element-1621" />
          <MapController flyTarget={flyTarget} data-id="element-1622" />
          <MapCursorTooltip overlay={selectedOverlay} data-id="element-1623" />
        </MapContainer>

        {/* Top Controls — hidden in readOnly */}
        {!readOnly && <div className={`absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none ${actionsMenuOpen || layersPanelOpen || poiPanelOpen ? 'z-[1100]' : 'z-[1000]'}`} data-id="element-1624">
            {/* Left controls — hidden in compact mode */}
            {!isCompact && <div className="flex items-center gap-2 pointer-events-auto min-w-0 overflow-hidden flex-1 mr-2" data-id="element-1625">
                {/* Overlay Dropdown */}
                <div className="relative flex-shrink-0 min-w-0" style={{
          maxWidth: isMedium ? '200px' : '280px'
        }} data-id="element-1626">
                  <button ref={overlayButtonRef} onClick={() => {
            setOverlayMenuOpen(!overlayMenuOpen);
            setLayersPanelOpen(false);
            setPoiPanelOpen(false);
          }} className={cn('flex items-center gap-3 px-4 py-2.5 rounded-full shadow-2xl text-sm font-semibold transition-all whitespace-nowrap w-full min-w-0', isLight ? 'shadow-slate-300/40 text-slate-800 hover:bg-black/[0.04] border border-black/[0.08]' : 'shadow-black/40 text-white/90 hover:bg-white/[0.03] border border-white/[0.08]')} style={glassStyle} data-id="element-1627">
                    <span className="truncate" data-id="element-1628">{selectedOverlay}</span>
                    <ChevronDown size={16} className={isLight ? 'text-slate-400 flex-shrink-0' : 'text-white/40 flex-shrink-0'} data-id="element-1629" />
                  </button>
                  {overlayMenuOpen && ReactDOM.createPortal(<div className={cn('fixed w-72 rounded-2xl shadow-2xl overflow-hidden py-1', isLight ? 'shadow-slate-300/40 border border-black/[0.08]' : 'shadow-black/40 border border-white/[0.08]')} style={{
            ...portalGlassStyle,
            zIndex: 9999,
            top: overlayButtonRef.current ? overlayButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: overlayButtonRef.current ? overlayButtonRef.current.getBoundingClientRect().left : 0
          }} data-id="element-1630">
                        <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
              background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
            }} data-id="element-1631" />
                        {Object.keys(overlayConfigs).map(opt => <button key={opt} onClick={() => {
              setSelectedOverlay(opt);
              setOverlayMenuOpen(false);
              onParameterChange?.();
            }} className={cn('relative w-full px-4 py-3 text-left text-sm flex items-center justify-between transition-colors', isLight ? 'text-slate-600 hover:bg-black/[0.04] hover:text-slate-900' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-1632">
                            {opt}
                            {selectedOverlay === opt && <div className="w-2 h-2 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1633" />}
                          </button>)}
                      </div>, document.body)}
                </div>

                {/* Layers Button */}
                <div className="relative" data-id="element-1634">
                  <button ref={layersButtonRef} onClick={() => {
            setLayersPanelOpen(!layersPanelOpen);
            setOverlayMenuOpen(false);
            setPoiPanelOpen(false);
          }} className={cn('p-2.5 rounded-full shadow-2xl transition-colors', isLight ? 'shadow-slate-300/40 border border-black/[0.08] hover:bg-black/[0.04]' : 'shadow-black/40 border border-white/[0.08] hover:bg-white/[0.06]', layersPanelOpen || activeLayers.size > 0 ? 'text-[#EC4899]' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-white/50 hover:text-white/90')} style={glassStyle} title="Weather Layers" data-id="element-1635">
                    <Layers size={16} data-id="element-1636" />
                    {activeLayers.size > 0 && <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#EC4899] rounded-full flex items-center justify-center" data-id="element-1637">
                        <span className="text-[8px] text-white font-bold" data-id="element-1638">
                          {activeLayers.size}
                        </span>
                      </div>}
                  </button>
                  {layersPanelOpen && ReactDOM.createPortal(<div ref={layersPanelRef} className={cn('fixed w-72 rounded-2xl shadow-2xl overflow-hidden', isLight ? 'shadow-slate-300/50 border border-black/[0.08]' : 'shadow-black/50 border border-white/[0.08]')} style={{
            ...portalGlassStyle,
            zIndex: 9999,
            top: layersButtonRef.current ? layersButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: layersButtonRef.current ? layersButtonRef.current.getBoundingClientRect().left : 0
          }} data-id="element-1639">
                        <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
              background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
            }} data-id="element-1640" />
                        <div className={cn('relative flex items-center justify-between px-4 py-3 border-b', isLight ? 'border-black/[0.06]' : 'border-white/[0.06]')} data-id="element-1641">
                          <span className={cn('text-sm font-semibold', isLight ? 'text-slate-800' : 'text-white/90')} data-id="element-1642">
                            Layers
                          </span>
                          <button onClick={() => setLayersPanelOpen(false)} className={cn('p-1 rounded-md transition-colors', isLight ? 'text-slate-400 hover:text-slate-600 hover:bg-black/[0.06]' : 'text-white/40 hover:text-white/80 hover:bg-white/[0.08]')} data-id="element-1643">
                            <X size={14} data-id="element-1644" />
                          </button>
                        </div>
                        <div className="relative p-2 space-y-0.5" data-id="element-1645">
                          {WEATHER_LAYERS.map(layer => {
                const isActive = activeLayers.has(layer.id);
                const isDuplicateOfBase = selectedOverlay === 'Air Temperature 2m' && layer.id === 'temperature' || selectedOverlay === 'Air Pressure Sea Level' && layer.id === 'pressure' || selectedOverlay === 'Surface Solar Radiation' && layer.id === 'temperature';
                return <button key={layer.id} onClick={() => !isDuplicateOfBase && toggleLayer(layer.id)} className={cn('w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors', isDuplicateOfBase ? 'opacity-40 cursor-not-allowed' : isLight ? 'hover:bg-black/[0.04]' : 'hover:bg-white/[0.04]')} data-id="element-1646">
                                <div className="flex items-center gap-3" data-id="element-1647">
                                  {layer.icon}
                                  <div data-id="element-1648">
                                    <span className={cn('text-sm', isActive && !isDuplicateOfBase ? isLight ? 'text-slate-900 font-medium' : 'text-white font-medium' : isLight ? 'text-slate-600' : 'text-slate-300')} data-id="element-1649">
                                      {layer.label}
                                    </span>
                                    {isDuplicateOfBase && <span className={cn('text-[10px] block', isLight ? 'text-slate-400' : 'text-slate-500')} data-id="element-1650">
                                        Already active as base
                                      </span>}
                                  </div>
                                </div>
                                <div className={cn('w-10 h-[22px] rounded-full p-0.5 transition-colors', isDuplicateOfBase ? 'bg-slate-700/50' : isActive ? 'bg-[#EC4899]' : isLight ? 'bg-slate-300' : 'bg-slate-700')} data-id="element-1651">
                                  <div className={cn('w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform', isDuplicateOfBase ? 'translate-x-0 opacity-30' : isActive ? 'translate-x-[18px]' : 'translate-x-0')} data-id="element-1652" />
                                </div>
                              </button>;
              })}
                        </div>
                      </div>, document.body)}
                </div>

                {/* POI Button */}
                <div className="relative" data-id="element-1653">
                  <button ref={poiButtonRef} onClick={() => {
            setPoiPanelOpen(!poiPanelOpen);
            setOverlayMenuOpen(false);
            setLayersPanelOpen(false);
            setModelDropdownOpen(false);
          }} className={cn('p-2.5 rounded-full shadow-2xl transition-colors', isLight ? 'shadow-slate-300/40 border border-black/[0.08] hover:bg-black/[0.04]' : 'shadow-black/40 border border-white/[0.08] hover:bg-white/[0.06]', poiPanelOpen || activePOIs.size > 0 ? 'text-[#EC4899]' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-white/50 hover:text-white/90')} style={glassStyle} title="Points of Interest" data-id="element-1654">
                    <MapPin size={16} data-id="element-1655" />
                    {activePOIs.size > 0 && <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#EC4899] rounded-full flex items-center justify-center" data-id="element-1656">
                        <span className="text-[8px] text-white font-bold" data-id="element-1657">
                          {activePOIs.size}
                        </span>
                      </div>}
                  </button>
                  {poiPanelOpen && ReactDOM.createPortal(<div ref={poiPanelRef} className={cn('fixed w-80 rounded-2xl shadow-2xl overflow-visible', isLight ? 'shadow-slate-300/50 border border-black/[0.08]' : 'shadow-black/50 border border-white/[0.08]')} style={{
            ...portalGlassStyle,
            zIndex: 9999,
            top: poiButtonRef.current ? poiButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: poiButtonRef.current ? poiButtonRef.current.getBoundingClientRect().left : 0
          }} data-id="element-1658">
                        <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
              background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
            }} data-id="element-1659" />
                        <div className={cn('relative flex items-center justify-between px-4 py-3 border-b', isLight ? 'border-black/[0.06]' : 'border-white/[0.06]')} data-id="element-1660">
                          <div className="flex items-center gap-2" data-id="element-1661">
                            <MapPin size={14} className="text-[#EC4899]" data-id="element-1662" />
                            <span className={cn('text-sm font-semibold', isLight ? 'text-slate-800' : 'text-white/90')} data-id="element-1663">
                              Points of Interest
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1664">
                            <button onClick={toggleAllPOIs} className="text-[10px] font-medium text-[#EC4899] hover:text-[#f472b6] transition-colors px-2 py-1 rounded-md hover:bg-[#EC4899]/10" data-id="element-1665">
                              {activePOIs.size === POI_CATEGORIES.length ? 'Hide All' : 'Show All'}
                            </button>
                            <button onClick={() => setPoiPanelOpen(false)} className={cn('p-1 rounded-md transition-colors', isLight ? 'text-slate-400 hover:text-slate-600 hover:bg-black/[0.06]' : 'text-white/40 hover:text-white/80 hover:bg-white/[0.08]')} data-id="element-1666">
                              <X size={14} data-id="element-1667" />
                            </button>
                          </div>
                        </div>
                        <div className="relative p-2 space-y-0.5 max-h-[400px] overflow-y-auto" data-id="element-1668">
                          {POI_CATEGORIES.map(cat => {
                const isActive = activePOIs.has(cat.id);
                const count = POI_DATA.filter(p => p.categoryId === cat.id).length;
                return <button key={cat.id} onClick={() => togglePOI(cat.id)} className={cn('w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors', isLight ? 'hover:bg-black/[0.04]' : 'hover:bg-white/[0.04]')} data-id="element-1669">
                                <div className="flex items-center gap-3" data-id="element-1670">
                                  <div className="flex items-center justify-center w-7 h-7 rounded-lg" style={{
                      backgroundColor: `${cat.color}20`
                    }} data-id="element-1671">
                                    <div style={{
                        color: cat.color
                      }} data-id="element-1672">
                                      {cat.icon}
                                    </div>
                                  </div>
                                  <div className="text-left" data-id="element-1673">
                                    <span className={cn('text-sm block leading-tight', isActive ? isLight ? 'text-slate-900 font-medium' : 'text-white font-medium' : isLight ? 'text-slate-600' : 'text-slate-300')} data-id="element-1674">
                                      {cat.label}
                                    </span>
                                    <span className={cn('text-[10px]', isLight ? 'text-slate-400' : 'text-slate-500')} data-id="element-1675">
                                      {count} locations
                                    </span>
                                  </div>
                                </div>
                                <div className={cn('w-10 h-[22px] rounded-full p-0.5 transition-colors flex-shrink-0', isActive ? 'bg-[#EC4899]' : isLight ? 'bg-slate-300' : 'bg-slate-700')} data-id="element-1676">
                                  <div className={cn('w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-transform', isActive ? 'translate-x-[18px]' : 'translate-x-0')} data-id="element-1677" />
                                </div>
                              </button>;
              })}
                        </div>
                        {activePOIs.size > 0 && <>
                            <div className={cn('mx-4 h-px', isLight ? 'bg-black/[0.06]' : 'bg-white/[0.06]')} data-id="element-1678" />
                            <div className="relative px-4 py-3 flex items-center justify-between" data-id="element-1679">
                              <span className={cn('text-[11px]', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-1680">
                                {visiblePOIs.length} markers visible
                              </span>
                              <div className="flex gap-1.5" data-id="element-1681">
                                {POI_CATEGORIES.filter(c => activePOIs.has(c.id)).map(c => <div key={c.id} className="w-2.5 h-2.5 rounded-full" style={{
                    backgroundColor: c.color
                  }} title={c.label} data-id="element-1682" />)}
                              </div>
                            </div>
                          </>}
                      </div>, document.body)}
                </div>

                {/* Model Selector — hidden in medium mode */}
                {!isMedium && <div className="relative" data-id="element-1683">
                    <button ref={modelDropdownButtonRef} onClick={() => {
            setModelDropdownOpen(!modelDropdownOpen);
            setOverlayMenuOpen(false);
            setLayersPanelOpen(false);
            setPoiPanelOpen(false);
          }} className={cn('flex items-center gap-2 px-3 py-2.5 rounded-full shadow-2xl text-xs font-medium transition-all', isLight ? 'shadow-slate-300/40 text-slate-800 hover:bg-black/[0.04] border border-black/[0.08]' : 'shadow-black/40 text-white/90 hover:bg-white/[0.03] border border-white/[0.08]')} style={glassStyle} title="Select model" data-id="element-1684">
                      <Cpu size={14} className="text-[#EC4899]" data-id="element-1685" />
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
              backgroundColor: MODEL_COLORS[selectedMapModel] || '#EC4899'
            }} data-id="element-1686" />
                      <span className="font-semibold max-w-[100px] truncate" data-id="element-1687">
                        {selectedMapModel}
                      </span>
                      <ChevronDown size={12} className={isLight ? 'text-slate-400' : 'text-white/40'} data-id="element-1688" />
                    </button>
                    {modelDropdownOpen && ReactDOM.createPortal(<div ref={modelDropdownRef} className={cn('fixed w-56 rounded-xl shadow-2xl overflow-hidden py-1', isLight ? 'shadow-slate-300/50 border border-black/[0.08]' : 'shadow-black/50 border border-white/[0.08]')} style={{
            ...portalGlassStyle,
            zIndex: 9999,
            top: modelDropdownButtonRef.current ? modelDropdownButtonRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: modelDropdownButtonRef.current ? modelDropdownButtonRef.current.getBoundingClientRect().left : 0
          }} data-id="element-1689">
                          <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
              background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
            }} data-id="element-1690" />
                          <div className={cn('relative px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest', isLight ? 'text-slate-400' : 'text-white/30')} data-id="element-1691">
                            {modelOverride ? 'Selected Runs' : 'Available Models'}
                          </div>
                          {availableModels.map(model => {
              const isActive = selectedMapModel === model;
              const color = MODEL_COLORS[model] || '#94a3b8';
              return <button key={model} onClick={() => {
                setSelectedMapModel(model);
                setModelDropdownOpen(false);
                onParameterChange?.();
              }} className={cn('relative w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-colors', isLight ? 'text-slate-600 hover:bg-black/[0.04] hover:text-slate-900' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white')} data-id="element-1692">
                                <div className="flex items-center gap-2.5" data-id="element-1693">
                                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                    backgroundColor: color
                  }} data-id="element-1694" />
                                  <span className={isActive ? isLight ? 'text-slate-900 font-medium' : 'text-white font-medium' : ''} data-id="element-1695">
                                    {model}
                                  </span>
                                </div>
                                {isActive && <Check size={12} className="text-[#EC4899] flex-shrink-0" data-id="element-1696" />}
                              </button>;
            })}
                          {modelOverride && modelOverride.runs.length > 0 && <>
                              <div className={cn('h-px my-1', isLight ? 'bg-black/[0.06]' : 'bg-white/[0.06]')} data-id="element-1697" />
                              <div className={cn('px-3 py-1.5 text-[10px]', isLight ? 'text-slate-400' : 'text-slate-500')} data-id="element-1698">
                                {modelOverride.runs.length} run
                                {modelOverride.runs.length !== 1 ? 's' : ''}{' '}
                                from global selector
                              </div>
                            </>}
                        </div>, document.body)}
                  </div>}
              </div>}
            {isCompact && <div className="flex-1" data-id="element-1699" />}

            {/* Right actions — always visible */}
            <div className="flex items-center gap-2 pointer-events-auto flex-shrink-0" data-id="element-1700">
              {!isCompact && <button className={cn('p-2.5 rounded-full shadow-2xl transition-colors', isLight ? 'shadow-slate-300/40 text-slate-500 hover:text-slate-700 hover:bg-black/[0.04] border border-black/[0.08]' : 'shadow-black/40 text-white/50 hover:text-white/90 hover:bg-white/[0.06] border border-white/[0.08]')} style={glassStyle} title="Reset map position" data-id="element-1701">
                  <LocateFixed size={16} data-id="element-1702" />
                </button>}
              <div className="relative" ref={actionsRef} data-id="element-1703">
                <button className={cn('p-2.5 rounded-full shadow-2xl transition-colors', isLight ? 'shadow-slate-300/40 text-slate-500 hover:text-slate-700 hover:bg-black/[0.04] border border-black/[0.08]' : 'shadow-black/40 text-white/50 hover:text-white/90 hover:bg-white/[0.06] border border-white/[0.08]')} style={glassStyle} onClick={() => setActionsMenuOpen(!actionsMenuOpen)} data-id="element-1704">
                  <MoreVertical size={16} data-id="element-1705" />
                </button>
                {actionsMenuOpen && <MapActionsMenu isBackground={isBackground} onEditWithAI={() => setAiEditOpen(true)} onToggleBackground={onToggleBackground} onDuplicate={onDuplicate} onRemove={onRemove} onClose={() => setActionsMenuOpen(false)} showResetPosition={isCompact} onResetPosition={() => setFlyTarget({
            lat: 54,
            lng: -2,
            zoom: 6
          })} compactMode={isCompact} onOpenLayers={() => setLayersPanelOpen(true)} activeLayersCount={activeLayers.size} onOpenPOI={() => setPoiPanelOpen(true)} activePOIsCount={activePOIs.size} onOpenModel={() => setModelDropdownOpen(true)} selectedModel={selectedMapModel} showModelInMenu={isMedium} data-id="element-1706" />}
              </div>
            </div>
          </div>}

        {/* Contextual tip — hidden in readOnly */}
        {!readOnly && showTip && <div className="absolute top-16 left-4 right-4 z-[999] pointer-events-auto transition-all duration-500 ease-out" style={{
      opacity: tipVisible ? 1 : 0,
      transform: tipVisible ? 'translateY(0)' : 'translateY(-4px)'
    }} data-id="element-1707">
            <div className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-[11px]', isLight ? 'border border-[#EC4899]/20 text-slate-600' : 'border border-[#EC4899]/15 text-white/60')} style={{
        background: isLight ? 'rgba(255, 255, 255, 0.85)' : 'rgba(15, 23, 42, 0.7)',
        backdropFilter: 'blur(16px)'
      }} data-id="element-1708">
              <Lightbulb size={12} className="text-[#EC4899]/60 flex-shrink-0" data-id="element-1709" />
              <span data-id="element-1710">
                Use the{' '}
                <strong className={isLight ? 'text-slate-800' : 'text-white/70'} data-id="element-1711">
                  Layers
                </strong>{' '}
                and{' '}
                <strong className={isLight ? 'text-slate-800' : 'text-white/70'} data-id="element-1712">
                  POI
                </strong>{' '}
                buttons to overlay weather data and energy infrastructure
              </span>
              <button onClick={() => {
          setTipVisible(false);
          setTimeout(() => setShowTip(false), 400);
        }} className={cn('ml-auto p-0.5 flex-shrink-0', isLight ? 'text-slate-300 hover:text-slate-500' : 'text-white/30 hover:text-white/60')} data-id="element-1713">
                <X size={10} data-id="element-1714" />
              </button>
            </div>
          </div>}

        {/* Edit with AI Panel — hidden in readOnly */}
        {!readOnly && aiEditOpen && <MapAiEditPanel aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} aiResult={aiResult} aiLoading={aiLoading} onSubmit={handleAiEdit} onClose={() => {
      setAiEditOpen(false);
      setAiResult('');
      setAiPrompt('');
    }} data-id="element-1715" />}

        {/* Bottom panels — hidden in readOnly */}
        {!readOnly && <div className={`absolute bottom-10 left-4 right-4 z-[1000] flex pointer-events-none gap-3 ${isCompact ? 'flex-col items-stretch' : 'flex-row items-end justify-between'}`} data-id="element-1716">
            <div className={cn('pointer-events-auto p-3 rounded-2xl shadow-2xl w-64 flex-shrink-0', isLight ? 'shadow-slate-300/40 border border-black/[0.08]' : 'shadow-black/40 border border-white/[0.08]')} style={glassStyle} data-id="element-1717">
              <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
          background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
        }} data-id="element-1718" />
              <div className="relative" data-id="element-1719">
                <div className="flex items-center justify-between mb-2" data-id="element-1720">
                  <span className={cn('text-xs font-semibold', isLight ? 'text-slate-700' : 'text-white/80')} data-id="element-1721">
                    {config.legendTitle} [{config.legendUnit}]
                  </span>
                  <div className="relative" ref={colorScaleRef} data-id="element-1722">
                    <button onClick={() => setColorScaleOpen(!colorScaleOpen)} className={cn('text-[10px] px-2 py-0.5 rounded flex items-center gap-1 transition-colors', isLight ? 'bg-black/[0.06] text-slate-500 border border-black/[0.06] hover:bg-black/[0.10] hover:text-slate-700' : 'bg-white/[0.08] text-white/60 border border-white/[0.08] hover:bg-white/[0.12] hover:text-white/80')} data-id="element-1723">
                      {colorScaleMode === 'dynamic' ? 'Dynamic' : 'Fixed'}
                      <ChevronDown size={8} className={isLight ? 'text-slate-400' : 'text-white/40'} data-id="element-1724" />
                    </button>
                    {colorScaleOpen && <div className={cn('absolute bottom-full right-0 mb-1.5 w-28 rounded-lg shadow-2xl py-0.5 overflow-hidden', isLight ? 'shadow-slate-300/50 border border-black/[0.08]' : 'shadow-black/50 border border-white/[0.08]')} style={portalGlassStyle} data-id="element-1725">
                        <div className="absolute inset-0 rounded-lg pointer-events-none" style={{
                  background: isLight ? 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
                }} data-id="element-1726" />
                        <button onClick={() => {
                  setColorScaleMode('fixed');
                  setColorScaleOpen(false);
                }} className={cn('relative w-full px-3 py-1.5 text-left text-[11px] flex items-center justify-between transition-colors', isLight ? 'text-slate-600 hover:bg-black/[0.04] hover:text-slate-900' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-1727">
                          Fixed
                          {colorScaleMode === 'fixed' && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1728" />}
                        </button>
                        <button onClick={() => {
                  setColorScaleMode('dynamic');
                  setColorScaleOpen(false);
                }} className={cn('relative w-full px-3 py-1.5 text-left text-[11px] flex items-center justify-between transition-colors', isLight ? 'text-slate-600 hover:bg-black/[0.04] hover:text-slate-900' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-1729">
                          Dynamic
                          {colorScaleMode === 'dynamic' && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1730" />}
                        </button>
                      </div>}
                  </div>
                </div>
                <div className="h-2.5 w-full rounded-full mb-1" style={{
            background: config.gradient
          }} data-id="element-1731" />
                <div className={cn('flex justify-between text-[10px] font-mono', isLight ? 'text-slate-500' : 'text-white/50')} data-id="element-1732">
                  <span data-id="element-1733">{config.min}</span>
                  <span data-id="element-1734">{config.max}</span>
                </div>
              </div>
            </div>

            <div className={cn('flex items-center gap-2 pointer-events-auto px-3 py-2 rounded-full shadow-2xl min-w-0 overflow-hidden', isLight ? 'shadow-slate-300/40 border border-black/[0.08]' : 'shadow-black/40 border border-white/[0.08]', isCompact ? 'w-full' : '')} style={glassStyle} data-id="element-1735">
              <button className={cn('p-1 transition-colors flex-shrink-0', isLight ? 'text-slate-400 hover:text-slate-700' : 'text-white/40 hover:text-white/90')} data-id="element-1736">
                <ChevronLeft size={14} data-id="element-1737" />
              </button>
              <button className="p-1.5 bg-[#EC4899] text-white rounded-full hover:bg-[#db2777] transition-colors shadow-md shadow-pink-500/30 flex-shrink-0" onClick={() => setIsPlaying(!isPlaying)} data-id="element-1738">
                {isPlaying ? <Pause size={12} fill="currentColor" data-id="element-1739" /> : <Play size={12} fill="currentColor" data-id="element-1740" />}
              </button>
              <button className={cn('p-1 transition-colors flex-shrink-0', isLight ? 'text-slate-400 hover:text-slate-700' : 'text-white/40 hover:text-white/90')} data-id="element-1741">
                <ChevronRight size={14} data-id="element-1742" />
              </button>
              <div className={cn('flex-1 min-w-[48px] relative h-1 rounded-full', isLight ? 'bg-black/[0.08]' : 'bg-white/[0.1]')} data-id="element-1743">
                <div className="absolute left-0 top-0 bottom-0 w-1/3 bg-[#EC4899]/40 rounded-full" data-id="element-1744" />
                <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-[#EC4899] rounded-full shadow-[0_0_6px_rgba(236,72,153,0.4)] cursor-pointer hover:scale-125 transition-transform" data-id="element-1745" />
              </div>
              <span className={cn('text-[11px] font-medium tabular-nums flex-shrink-0 hidden sm:block', isLight ? 'text-slate-500' : 'text-white/50')} data-id="element-1746">
                01:00 +1h
              </span>
            </div>
          </div>}

        {/* Bottom Info Bar — hidden in readOnly */}
        {!readOnly && <div className={cn('absolute bottom-0 left-0 right-0 px-4 py-2 flex items-center justify-between text-xs z-[1001]', isLight ? 'border-t border-black/[0.06]' : 'border-t border-white/[0.06]')} style={glassStyleDark} data-id="element-1747">
            <div className={cn('flex items-center gap-2', isLight ? 'text-slate-500' : 'text-white/50')} data-id="element-1748">
              <Database size={12} data-id="element-1749" />
              <span className={cn('font-medium', isLight ? 'text-slate-700' : 'text-white/70')} data-id="element-1750">
                Base: {selectedOverlay}
              </span>
              <span className={isLight ? 'text-slate-300' : 'text-white/20'} data-id="element-1751">
                ·
              </span>
              <div className="flex items-center gap-1.5" data-id="element-1752">
                <div className="w-1.5 h-1.5 rounded-full" style={{
            backgroundColor: MODEL_COLORS[selectedMapModel] || '#EC4899'
          }} data-id="element-1753" />
                <span className={cn('font-medium', isLight ? 'text-slate-700' : 'text-white/70')} data-id="element-1754">
                  {selectedMapModel}
                </span>
              </div>
              {activeOWMTiles.filter(l => l.owmLayer !== owmLayer).length > 0 && <>
                  <span className={isLight ? 'text-slate-300' : 'text-white/20'} data-id="element-1755">
                    +
                  </span>
                  <span className="text-[#EC4899]/70" data-id="element-1756">
                    {activeOWMTiles.filter(l => l.owmLayer !== owmLayer).map(l => l.label).join(', ')}
                  </span>
                </>}
              <span className={isLight ? 'text-slate-300' : 'text-white/20'} data-id="element-1757">
                |
              </span>
              <span className="text-emerald-500" data-id="element-1758">OWM Live</span>
            </div>
            <Info size={12} className={isLight ? 'text-slate-300' : 'text-white/30'} data-id="element-1759" />
          </div>}
      </div>;
  if (isBackground) {
    return mapContent;
  }
  return <WidgetWrapper ref={ref} title="Europe Temperature & Pressure Map" onRemove={onRemove} onDoubleClickHandle={onDoubleClickHandle} readOnly={readOnly} style={style} className={className} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onTouchEnd={onTouchEnd} {...props} data-id="element-1760">
        {mapContent}
      </WidgetWrapper>;
});